import React from 'react';
import {
    Text,
    VStack,
    Box, Grid, GridItem, SimpleGrid,
} from '@chakra-ui/react';
import {
    Hide, KeyValuePair, Show, ThemeTokens,
} from '@acp/core-ui';
import { ReportedFindingDataMachine } from '@acp/shared';
import { ReportedFindingsMachineContext } from '../../machine/context';
import { ReportedFindingTag } from '../../reported-finding-tag-collection';
import {
    FindingDismissedTag,
} from '../../../../../property-details/reported-findings-tab/dismiss-reported-finding-button';
import { QuoteRequestedTag } from '../quote-requested-tag';
import { ServiceIcons } from '../../service-icons';
import { RowLayout } from './reported-findings-wizard-row-layout';

const COLORED_BORDER_WIDTH = 6;
const CARD_BORDER_PADDING = 5;

function GridWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={[
                `
                  "service"
                  "reported-findings"
                  "status"
                  "actions"
                `,
                null,
                null,
                null,
                '"service reported-findings status actions"', // xl
            ]}
            gridTemplateColumns={[
                '1fr', // base
                null, // small
                null,
                null, // large
                '15% 40% 23% 15%', // xl (4-column custom sizes)
            ]}
            gap={{ base: 2, m: 4 }}
            alignItems={{ base: 'center', xl: 'start' }}
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

function ReportedFindingsWizardRowHeader() {
    return (
        <Box
            borderBottomWidth={1}
            px={CARD_BORDER_PADDING}
            pb={2}
            fontSize={{ base: 12, lg: 13 }}
            w="full"
            borderLeftWidth={COLORED_BORDER_WIDTH}
            borderLeftColor="transparent"
        >
            <GridWrapper>
                <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                    <GridItem area="service">Service</GridItem>
                    <GridItem area="reported-findings">Reported findings</GridItem>
                    <GridItem area="status">Status</GridItem>
                    <GridItem area="actions">
                        <Text> </Text>
                    </GridItem>
                </Show>
            </GridWrapper>
        </Box>
    );
}

function ReportedFindingsWizardRow({
    reportedFinding,
    gridWrapper = GridWrapper,
    children,
}: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reportedFinding: ReportedFindingDataMachine;
    gridWrapper?: React.ComponentType<{ children: React.ReactNode }>;
    children?: React.ReactNode;
}) {
    const rawFindingResponse = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.apiResponse
    );

    return (
        <RowLayout borderLeftColor={reportedFinding.servicingCategory.color}>
            <RowLayout.Body as={gridWrapper}>
                <GridItem>
                    <RowLayout.Column area="service">
                        <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                            <SimpleGrid columns={{ base: 2 }} spacing={2}>
                                {rawFindingResponse?.service && (
                                    <ServiceIcons service={rawFindingResponse!.service} />
                                )}
                            </SimpleGrid>
                        </Show>
                        <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                            <KeyValuePair label="Service" align="flex-start" mt={2}>
                                <SimpleGrid columns={{ base: 2 }} spacing={2}>
                                    {rawFindingResponse?.service && (
                                        <ServiceIcons service={rawFindingResponse!.service} />
                                    )}
                                </SimpleGrid>
                            </KeyValuePair>
                        </Hide>
                    </RowLayout.Column>
                </GridItem>
                <RowLayout.Column area="reported-findings">
                    <VStack align="left" gap={0}>
                        <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                            <Text mb={1}>{reportedFinding.originalProblemItem.title}</Text>
                        </Show>
                        <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                            <KeyValuePair label="Reported findings" align="flex-start" spacing={1} mt={2}>
                                <Text mb={1}>{reportedFinding.originalProblemItem.title}</Text>
                            </KeyValuePair>
                        </Hide>
                        <Text color="muted.500" fontSize="xs">
                            {reportedFinding.appliance?.applianceName ?? 'N/A'}
                        </Text>
                        <Text color="muted.500" fontSize="xs">
                            Location:
                            {' '}
                            {reportedFinding.appliance?.applianceLocation ?? 'N/A'}
                        </Text>
                    </VStack>
                </RowLayout.Column>
                <RowLayout.Column area="status" mt={{ base: 2, xl: 0 }}>
                    <ReportedFindingTag reportedFinding={reportedFinding} />
                    <FindingDismissedTag reportedFinding={reportedFinding} />
                    <QuoteRequestedTag reportedFinding={reportedFinding} />
                </RowLayout.Column>
                <GridItem
                    area="actions"
                    display="flex"
                    w="100%"
                    justifyContent="flex-end"
                >
                    {children}
                </GridItem>
            </RowLayout.Body>
        </RowLayout>
    );
}

export { ReportedFindingsWizardRowHeader, ReportedFindingsWizardRow };
